/**
 * Fellows Corner mentor metadata overrides.
 *
 * Mentor membership comes from `GET /v2/mentors` (the backend's curated
 * roster). Entries here add Fellows-specific copy, presentation, and optional
 * routing overrides for known mentors; a platform mentor does not need an
 * entry to appear or receive inquiries.
 *
 * Relay routing remains available for a future non-user mentor, but the current
 * database-driven directory only discovers platform users.
 */

import { env } from '@/util/env';

/**
 * Cancer focus mentorship inquiries are filed under, per environment.
 *
 * Cancer type ids are database primary keys and are not aligned across
 * environments, so a single literal is wrong in every environment but the one it
 * was written for. Keyed here rather than derived on the backend because the
 * create request still requires the caller to supply a focus.
 *
 * Production points at the general focus because it has no mentorship-specific
 * cancer type yet. Swap it for a dedicated one, hidden from the cancer type
 * pickers, once that exists in production.
 *
 * TODO: Move this to the backend. A client should not carry database primary
 * keys that differ per environment, and this map has to be edited and shipped
 * whenever either environment's data changes. The API already receives the
 * mentorship inquiry type on the request, so it can resolve the focus itself
 * once supplying one is optional.
 */
const CANCER_FOCUS_ID_BY_APP_ENV: Record<string, number> = {
  development: 499,
  staging: 499,
  local: 499,
  'local-prod': 168,
  production: 168,
};

export const FELLOWS_CORNER_CANCER_FOCUS_ID =
  CANCER_FOCUS_ID_BY_APP_ENV[env.EXPO_PUBLIC_APP_ENV] ?? 499;

/**
 * Query param the Fellows Corner page reads to preselect a mentor on load,
 * so deep links (e.g. the global banner's CTA) can land on a specific mentor.
 */
export const FELLOWS_MENTOR_PARAM = 'mentor';

/** Dr. Gao's mentor id — the mentor the global banner introduces and links to. */
export const FELLOW_MENTOR_DR_GAO_ID = '3469';

export interface FellowMentor {
  /** Route param. For platform-expert mentors this is the expert id as a string. */
  id: string;
  name: string;
  specialty: string;
  title?: string;
  /**
   * Drives the profile-detail icons (role and institution) since these mentors
   * span professions: a clinician gets clinical icons, an attorney gets legal
   * ones. Needed because `identifiesAsDoctor` is false for both today and can't
   * distinguish them.
   */
  profession?: 'clinician' | 'attorney';
  /**
   * Real Primum expert id when the mentor is a platform user; `null` for
   * non-user mentors. When present, inquiries route directly to this expert.
   */
  expertId: number | null;
  /**
   * For non-user mentors, the Primum Support expert id their inquiries are
   * relayed through. Must be set before a non-user mentor can receive
   * inquiries; until then submission is blocked. Ignored when `expertId` is set.
   */
  relayExpertId?: number;
  /** Cancer focus the inquiry is filed under. */
  cancerFocusId: number;
  /**
   * Fellows-specific presentation data the expert directory does not carry.
   * `helpTags` are the "can help with" chips; `blurb` is a short bio shown
   * when the directory profile has none yet.
   */
  helpTags?: string[];
  blurb?: string;
  /**
   * Ask-form placeholder tailored to this mentor's domain. A short question
   * stub (matching the Medical On Demand style) so the prompt fits the mentor
   * rather than reading generically across a clinician and an attorney. Falls
   * back to `FELLOWS_QUESTION_PLACEHOLDER` when unset.
   */
  questionPlaceholder?: string;
}

/**
 * When the Fellows Corner nav badge stops claiming the feature is new.
 *
 * One week, and the only control there is: the badge does not clear on a visit,
 * because new describes the feature rather than the reader's relationship to it.
 * Nobody can dismiss it, so the window is short enough that seeing it every day
 * for its whole run stays reasonable.
 *
 * TEMPORARY: delete this and the badge with it once the date passes. A new
 * marker that outlives its claim trains people to stop reading badges.
 */
export const FELLOWS_CORNER_NEW_UNTIL = new Date('2026-08-19T00:00:00Z');

export const FELLOW_MENTORS_DATA = [
  {
    id: '3469',
    name: 'Dr. Jackson Gao',
    specialty: 'Community Oncologist',
    expertId: 3469,
    cancerFocusId: FELLOWS_CORNER_CANCER_FOCUS_ID,
    profession: 'clinician',
    helpTags: ['Career planning', 'Research', 'Fellowship → attending'],
    blurb:
      'Happy to talk through academic vs community practice, building a research niche, and the transition to attending.',
    questionPlaceholder: 'How should I think about academic vs. community practice…',
  },
  {
    id: '3476',
    name: 'Dr. Shelby Pruitt',
    specialty: 'Contracting',
    title: 'Attorney',
    expertId: 3476,
    cancerFocusId: FELLOWS_CORNER_CANCER_FOCUS_ID,
    profession: 'attorney',
    helpTags: ['Contracts', 'Employment', 'Negotiation'],
    blurb:
      'Health-care attorney who can help with employment contracts, negotiation, and early-career legal questions.',
    questionPlaceholder: 'How do I approach negotiating my first contract…',
  },
] as const satisfies readonly FellowMentor[];

export const FELLOW_MENTORS: FellowMentor[] = [...FELLOW_MENTORS_DATA];

/** Ask-form placeholder used when a mentor has no domain-specific one. */
export const FELLOWS_QUESTION_PLACEHOLDER = 'Ask your career or mentorship question…';

/**
 * Example questions shown as tappable chips. Selecting one opens the target
 * mentor's inline ask form with `message` prefilled, turning the prompts into
 * a real entry into the ask flow rather than decoration.
 */
export const FELLOWS_PROMPTS = [
  {
    label: 'Academic vs Community?',
    message: 'How should I think about choosing between academic and community practice?',
    mentorId: '3469',
  },
  {
    label: 'How do I negotiate my first contract?',
    message: 'How should I approach negotiating my first contract?',
    mentorId: '3469',
  },
  {
    label: 'Should I pursue research?',
    message: 'How do I decide whether to pursue research during and after fellowship?',
    mentorId: '3469',
  },
  {
    label: 'What does industry look like?',
    message: 'What does a career in industry look like, and how would I move toward one?',
    mentorId: '3469',
  },
  {
    label: 'Will community practice limit academia later?',
    message:
      'If I chose community or private practice right after fellowship, would it hurt my chances to get into academia or a BMT fellowship later?',
    mentorId: '3469',
  },
  {
    label: 'What career trajectory should I expect?',
    message:
      'What career trajectory should I expect, and how should I approach it stepwise? What opportunities does an early-career faculty member have available, whether they work in the community or at an academic center?',
    mentorId: '3469',
  },
  {
    label: 'An academic career in education, not research?',
    message:
      'How do I craft an academic career that focuses on medical education rather than research, but still continue to advance in academia?',
    mentorId: '3469',
  },
  {
    label: 'What mistakes did you make early on?',
    message: 'What mistakes did you make early in your career?',
    mentorId: '3469',
  },
  {
    label: 'What would you do differently?',
    message: 'What would you do differently if you were in my position now?',
    mentorId: '3469',
  },
] as const;

/**
 * Question/answer shape for a mentor's seed Q&A, authored as the Fellows
 * Corner flag's value in Flagsmith rather than shipped in the app — see
 * `hooks/useFellowSeedQA.ts`. There is no in-repo fallback set: until a real,
 * mentor-approved value is set for an environment, a fellow sees the profile
 * without a Questions tab rather than placeholder content.
 */
export interface FellowSeedQA {
  /** Registry mentor id the question and answer belong to. */
  mentorId: string;
  question: string;
  answer: string;
}

/**
 * Guidelines shown before submitting a mentorship inquiry: a short intro, what
 * the surface is for, and the limits of the advice given on it.
 *
 * Deliberately carries no sample exchange. Each mentor's own seed questions and
 * answers show what a mentorship conversation reads like, in their voice and
 * about their subject, which one edited sample standing in for everyone cannot.
 */
export const FELLOWS_CORNER_GUIDELINES = {
  title: 'Mentorship Guidelines',
  intro:
    'Fellows Corner is for candid career and business-of-medicine questions — the kind you can’t Google. Ask what a mentor would do in your position, from a career decision to the contract side of a first job. Not for patient recommendations.',
  sections: [
    {
      heading: 'Great questions to ask',
      items: [
        'Academic vs. community vs. industry paths',
        'Choosing a subspecialty or research niche',
        'Offers, contracts, and what’s worth negotiating',
        'The fellowship-to-attending transition',
        'Trade-offs mentors wish they’d known earlier',
      ],
    },
  ],
  disclaimer:
    'Mentors share general guidance from experience. It isn’t formal legal, financial, or clinical advice, and no attorney–client relationship is created.',
} as const;

export const getFellowMentor = (mentorId: string): FellowMentor | undefined =>
  FELLOW_MENTORS.find((mentor) => mentor.id === mentorId);

/**
 * Resolves the expert id an inquiry to this mentor should be sent to: the
 * mentor's own expert id when they're a platform user, otherwise the Primum
 * Support relay expert. Returns `null` when a non-user mentor has no relay
 * configured, in which case the mentor cannot yet receive inquiries.
 */
export const getMentorRecipientExpertId = (mentor: FellowMentor): number | null =>
  mentor.expertId ?? mentor.relayExpertId ?? null;

/**
 * A mentor reduced to what the inquiry form needs: an identity for the
 * `mentor_inquiry` data tag and the resolved recipient expert id.
 * `recipientExpertId` is `null` when the mentor cannot yet receive inquiries.
 */
export interface MentorTarget {
  id: string;
  name: string;
  recipientExpertId: number | null;
}

/**
 * Maps a Fellows Corner expert (sourced from the users endpoint) to an inquiry
 * target. A mentor present in the registry uses its routing override (e.g. a
 * non-user mentor's Primum Support relay); any other platform expert is a
 * platform user, so the recipient is simply their own expert id.
 */
export const resolveMentorTargetFromExpert = (expert: {
  id: string;
  name: string;
}): MentorTarget => {
  const registryMentor = getFellowMentor(expert.id);

  const recipientExpertId = registryMentor
    ? getMentorRecipientExpertId(registryMentor)
    : Number(expert.id);

  return {
    id: expert.id,
    name: registryMentor?.name ?? expert.name,
    recipientExpertId: Number.isNaN(recipientExpertId) ? null : recipientExpertId,
  };
};
